import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Trophy, Clock, User, CheckCircle, XCircle, RefreshCw, Filter, X, Search, Calendar, MapPin, Download } from "lucide-react";
import { toast } from "sonner";

export default function SetupLog({ selectedEvent }) {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const token = localStorage.getItem("token");
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filterUser, setFilterUser] = useState("");
  const [filterPointType, setFilterPointType] = useState("all");
  const [filterPhase, setFilterPhase] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  useEffect(() => {
    loadLogs();
  }, [selectedEvent?.id]);
  
  // Beregn unikke brugere og punkttyper fra logs
  const uniqueUsers = useMemo(() => {
    const users = [...new Set(logs.map(l => l.user_name).filter(Boolean))];
    return users.sort();
  }, [logs]);
  
  const uniquePointTypes = useMemo(() => {
    const types = [...new Set(logs.map(l => l.point_type).filter(Boolean))];
    return types.sort();
  }, [logs]);
  
  // Filtrerede logs
  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      // Bruger-filter
      if (filterUser && !log.user_name?.toLowerCase().includes(filterUser.toLowerCase())) {
        return false;
      }
      // Punkt-type filter
      if (filterPointType !== "all" && log.point_type !== filterPointType) {
        return false;
      }
      // Fase-filter
      if (filterPhase !== "all" && log.phase !== filterPhase) {
        return false;
      }
      // Dato fra
      if (filterDateFrom) {
        const logDate = new Date(log.timestamp);
        const fromDate = new Date(filterDateFrom);
        if (logDate < fromDate) return false;
      }
      // Dato til
      if (filterDateTo) {
        const logDate = new Date(log.timestamp);
        const toDate = new Date(filterDateTo);
        toDate.setHours(23, 59, 59); // Inkluder hele dagen
        if (logDate > toDate) return false;
      }
      return true;
    });
  }, [logs, filterUser, filterPointType, filterPhase, filterDateFrom, filterDateTo]);
  
  // Tjek om der er aktive filtre
  const hasActiveFilters = filterUser || filterPointType !== "all" || filterPhase !== "all" || filterDateFrom || filterDateTo;
  
  // Nulstil alle filtre
  const clearFilters = () => {
    setFilterUser("");
    setFilterPointType("all");
    setFilterPhase("all");
    setFilterDateFrom("");
    setFilterDateTo("");
  };

  const loadLogs = async () => {
    if (!selectedEvent?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/events/${selectedEvent.id}/setup-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(res.data.logs || []);
      setLeaderboard(res.data.leaderboard || []);
      setTotalLogs(res.data.total_logs || 0);
    } catch (error) {
      console.error("Kunne ikke hente historik:", error);
    } finally {
      setLoading(false);
    }
  };

  // Eksporter til CSV
  const exportToCSV = () => {
    if (filteredLogs.length === 0) {
      toast.error("Ingen data at eksportere");
      return;
    }
    
    setExporting(true);
    try {
      // CSV header
      const headers = ["Tidspunkt", "Bruger", "Handling", "Punkt", "Item", "Antal", "Total", "Fase", "Punkt-type"];
      
      // Format data
      const rows = filteredLogs.map(log => {
        const timestamp = log.timestamp ? new Date(log.timestamp).toLocaleString("da-DK") : "";
        const action = log.checked_count > 0 ? "Opsat" : "Fjernet";
        return [
          timestamp,
          log.user_name || "",
          action,
          log.point_name || "",
          log.item_name || "",
          log.checked_count || 0,
          log.total_count || 0,
          getPhaseLabel(log.phase),
          log.point_type || ""
        ];
      });
      
      // Byg CSV string
      const csvContent = [
        headers.join(";"),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(";"))
      ].join("\n");
      
      // Download fil
      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const eventName = selectedEvent?.name?.replace(/[^a-zA-Z0-9]/g, "_") || "event";
      const dateStr = new Date().toISOString().split("T")[0];
      link.download = `historik_${eventName}_${dateStr}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Eksporteret ${filteredLogs.length} rækker til CSV`);
    } catch (error) {
      console.error("Eksport fejlede:", error);
      toast.error("Kunne ikke eksportere data");
    } finally {
      setExporting(false);
    }
  };

  // Eksporter leaderboard til CSV
  const exportLeaderboardToCSV = () => {
    if (leaderboard.length === 0) {
      toast.error("Ingen leaderboard data at eksportere");
      return;
    }
    
    try {
      const headers = ["Placering", "Navn", "Email", "Afkrydsninger"];
      const rows = leaderboard.map((user, idx) => [
        idx + 1,
        user.name || "",
        user.email || "",
        user.checked || 0
      ]);
      
      const csvContent = [
        headers.join(";"),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(";"))
      ].join("\n");
      
      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const eventName = selectedEvent?.name?.replace(/[^a-zA-Z0-9]/g, "_") || "event";
      const dateStr = new Date().toISOString().split("T")[0];
      link.download = `leaderboard_${eventName}_${dateStr}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Eksporteret ${leaderboard.length} frivillige til CSV`);
    } catch (error) {
      console.error("Eksport fejlede:", error);
      toast.error("Kunne ikke eksportere leaderboard");
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Lige nu";
    if (diffMins < 60) return `${diffMins} min siden`;
    if (diffHours < 24) return `${diffHours} timer siden`;
    if (diffDays < 7) return `${diffDays} dage siden`;
    
    return date.toLocaleDateString("da-DK", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getPhaseLabel = (phase) => {
    const labels = {
      'setup': 'Opbygning',
      'teardown': 'Nedpakning',
      'operation': 'Afvikling',
      'planning': 'Planlægning'
    };
    return labels[phase] || phase;
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-card">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold font-mono uppercase">Historik</h1>
              <p className="text-xs text-zinc-500 font-mono">
                Opbygning & Nedpakning
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadLogs}
              className="ml-auto"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Opdater
            </Button>
          </div>
        </div>
      </header>

      <main className="p-4 space-y-6">
        {/* Leaderboard */}
        <section className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border bg-gradient-to-r from-yellow-500/10 to-orange-500/10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                  <h2 className="font-bold font-mono uppercase">Leaderboard</h2>
                </div>
                <p className="text-xs text-zinc-500 mt-1">Top bidragydere til opbygning/nedpakning</p>
              </div>
              {leaderboard.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportLeaderboardToCSV}
                  className="text-xs"
                  data-testid="export-leaderboard-btn"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Eksporter
                </Button>
              )}
            </div>
          </div>
          
          <div className="divide-y divide-border">
            {leaderboard.length === 0 ? (
              <div className="p-8 text-center text-zinc-500">
                <Trophy className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p className="font-mono">Ingen aktivitet endnu</p>
              </div>
            ) : (
              leaderboard.slice(0, 10).map((user, idx) => (
                <div 
                  key={user.email} 
                  className={`flex items-center gap-4 p-4 ${idx < 3 ? 'bg-yellow-500/5' : ''}`}
                  data-testid={`leaderboard-${idx}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    idx === 0 ? 'bg-yellow-500 text-black' :
                    idx === 1 ? 'bg-zinc-400 text-black' :
                    idx === 2 ? 'bg-orange-600 text-white' :
                    'bg-zinc-800 text-zinc-400'
                  }`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">{user.name}</p>
                    <p className="text-xs text-zinc-500 truncate">{user.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-500">{user.checked}</p>
                    <p className="text-xs text-zinc-500">afkrydsninger</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Aktivitetslog */}
        <section className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                <h2 className="font-bold font-mono uppercase">Seneste Aktivitet</h2>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportToCSV}
                  disabled={exporting || filteredLogs.length === 0}
                  className="text-xs"
                  data-testid="export-logs-btn"
                >
                  <Download className={`h-4 w-4 mr-1 ${exporting ? 'animate-pulse' : ''}`} />
                  {exporting ? 'Eksporterer...' : 'CSV'}
                </Button>
                <Button
                  variant={showFilters ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="text-xs"
                  data-testid="toggle-filters-btn"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                  {hasActiveFilters && (
                    <span className="ml-2 px-1.5 py-0.5 bg-primary-foreground text-primary rounded-full text-xs">
                      !
                    </span>
                  )}
                </Button>
              </div>
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              {hasActiveFilters 
                ? `Viser ${filteredLogs.length} af ${totalLogs} handlinger`
                : `${totalLogs} handlinger i alt`
              }
            </p>
          </div>
          
          {/* Filter panel */}
          {showFilters && (
            <div className="p-4 bg-zinc-900/50 border-b border-border space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Bruger søgning */}
                <div>
                  <label className="text-xs text-zinc-500 font-mono uppercase mb-1 block">Bruger</label>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                    <Input
                      placeholder="Søg efter navn..."
                      value={filterUser}
                      onChange={(e) => setFilterUser(e.target.value)}
                      className="pl-8 h-9 text-sm"
                      data-testid="filter-user-input"
                    />
                  </div>
                </div>
                
                {/* Punkt-type */}
                <div>
                  <label className="text-xs text-zinc-500 font-mono uppercase mb-1 block">Punkt-type</label>
                  <select
                    value={filterPointType}
                    onChange={(e) => setFilterPointType(e.target.value)}
                    className="w-full h-9 rounded-md border border-input bg-input/50 px-3 text-sm"
                    data-testid="filter-point-type"
                  >
                    <option value="all">Alle typer</option>
                    {uniquePointTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                
                {/* Fase */}
                <div>
                  <label className="text-xs text-zinc-500 font-mono uppercase mb-1 block">Fase</label>
                  <select
                    value={filterPhase}
                    onChange={(e) => setFilterPhase(e.target.value)}
                    className="w-full h-9 rounded-md border border-input bg-input/50 px-3 text-sm"
                    data-testid="filter-phase"
                  >
                    <option value="all">Alle faser</option>
                    <option value="setup">Opbygning</option>
                    <option value="teardown">Nedpakning</option>
                  </select>
                </div>
                
                {/* Dato fra */}
                <div>
                  <label className="text-xs text-zinc-500 font-mono uppercase mb-1 block">Dato fra</label>
                  <Input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    className="h-9 text-sm"
                    data-testid="filter-date-from"
                  />
                </div>
                
                {/* Dato til */}
                <div>
                  <label className="text-xs text-zinc-500 font-mono uppercase mb-1 block">Dato til</label>
                  <Input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    className="h-9 text-sm"
                    data-testid="filter-date-to"
                  />
                </div>
              </div>
              
              {/* Nulstil knap */}
              {hasActiveFilters && (
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-xs text-zinc-400 hover:text-white"
                    data-testid="clear-filters-btn"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Nulstil filtre
                  </Button>
                </div>
              )}
            </div>
          )}
          
          <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
            {filteredLogs.length === 0 ? (
              <div className="p-8 text-center text-zinc-500">
                <Clock className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p className="font-mono">
                  {hasActiveFilters ? 'Ingen resultater matcher filtrene' : 'Ingen aktivitet endnu'}
                </p>
                {hasActiveFilters && (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={clearFilters}
                    className="mt-2 text-primary"
                  >
                    Nulstil filtre
                  </Button>
                )}
              </div>
            ) : (
              filteredLogs.map((log, idx) => (
                <div 
                  key={log.id || idx} 
                  className="p-4 hover:bg-zinc-800/30 transition-colors"
                  data-testid={`log-${idx}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      log.checked_count > 0 ? 'bg-green-500/20' : 'bg-red-500/20'
                    }`}>
                      {log.checked_count > 0 ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">
                        <span className="text-primary">{log.user_name}</span>
                        {' '}
                        <span className={log.checked_count > 0 ? 'text-green-500' : 'text-red-500'}>
                          {log.action || (log.checked_count > 0 ? `opsat ${log.checked_count}/${log.total_count}` : 'fjernede')}
                        </span>
                      </p>
                      <p className="text-sm text-zinc-400 truncate">
                        {log.point_name} • {log.item_name}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          log.phase === 'setup' ? 'bg-blue-500/20 text-blue-400' :
                          log.phase === 'teardown' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-zinc-500/20 text-zinc-400'
                        }`}>
                          {getPhaseLabel(log.phase)}
                        </span>
                        {log.point_type && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-700/50 text-zinc-300">
                            {log.point_type}
                          </span>
                        )}
                        <span className="text-xs text-zinc-500">
                          {formatTime(log.timestamp)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
